import React from 'react'
import 'lib/ui/form/HTMLEditor/html-content.css'
import styled from 'styled-components'
import {useAttendeeVariables} from 'Event'
import {useCardsTemplate} from 'Event/template/Cards'
import {useToggle} from 'lib/toggle'
import {PointsSummaryConfig} from 'Event/template/Cards/Dashboard/Sidebar/SidebarItem/PointsSummary/PointsSummaryConfig'
import EditModeOnly from 'Event/Dashboard/editor/views/EditModeOnly'
import Button from '@material-ui/core/Button'
import {RemoveButton} from 'organization/Event/DashboardConfig/ConfigDialog'
import Section from 'Event/template/Cards/Dashboard/Sidebar/Section'
import {
  HasOffset,
  useEditSidebarItem,
} from 'Event/template/Cards/Dashboard/Sidebar/SidebarItem'
import {Ordered} from 'lib/list'
import {HasRules} from 'Event/attendee-rules'
import {useEditMode} from 'Event/EditModeProvider'
import {HasSchedule} from 'lib/ui/layout/Scheduled'

export const POINTS_SUMMARY = 'Points Summary'
export interface PointsSummaryProps
  extends Ordered,
    HasRules,
    HasOffset,
    HasSchedule {
  type: typeof POINTS_SUMMARY
  description: string
  summary?: string
  isEnabled: boolean
  logo: string | null
}

export const createPointsSummary = (): PointsSummaryProps => ({
  type: POINTS_SUMMARY,
  description: '',
  summary: '',
  isEnabled: true,
  logo: null,
  offsetTop: 0,
  offsetBottom: 0,
})

export default function PointsSummary(
  props: PointsSummaryProps & {id: string},
) {
  const {sidebar} = useCardsTemplate()
  const {summary, description, isEnabled, logo, offsetTop, offsetBottom} = props
  const v = useAttendeeVariables()
  const {flag: configVisible, toggle: toggleConfig} = useToggle()
  const isEditMode = useEditMode()

  if (!isEnabled && !isEditMode) {
    return null
  }

  return (
    <Section offsetTop={offsetTop} offsetBottom={offsetBottom}>
      <EditModeOnly>
        <PointsSummaryConfig
          showing={configVisible}
          onClose={toggleConfig}
          points={props}
          id={props.id}
        />
        <EditButton
          onClick={toggleConfig}
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          aria-label="edit points summary"
        >
          Edit Points Summary
        </EditButton>
        <RemovePointsSummarybutton />
      </EditModeOnly>
      <PointsLogo logo={logo} />
      <Box textColor={sidebar.textColor}>
        <Summary
          aria-label="points summary"
          textColor={sidebar.textColor}
          linkTextColor={sidebar.linkTextColor}
        >
          {v(summary)}
        </Summary>
        <Description
          textColor={sidebar.textColor}
          linkTextColor={sidebar.linkTextColor}
        >
          {v(description)}
        </Description>
      </Box>
    </Section>
  )
}

function RemovePointsSummarybutton() {
  const {remove: removeItem} = useEditSidebarItem()
  return (
    <RemoveButton size="large" showing onClick={removeItem}>
      Remove Points Summary
    </RemoveButton>
  )
}

function PointsLogo(props: {logo: string | null}) {
  if (!props.logo) {
    return null
  }
  return <HeaderImage src={props.logo} alt="Points Image" />
}

const Box = styled.div<{textColor: string | null}>`
  color: ${(props) => props.textColor};
`

const HeaderImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  margin-bottom: ${(props) => props.theme.spacing[3]};
`

const Container = React.forwardRef<
  HTMLDivElement,
  {
    className?: string
    children: string
    'aria-label'?: string
  }
>((props, ref) => {
  return (
    <div
      className={`html-content ${props.className}`}
      ref={ref}
      aria-label={props['aria-label']}
      dangerouslySetInnerHTML={{
        __html: props.children,
      }}
    />
  )
})

const Summary = styled(Container)<{
  textColor: string | null
  linkTextColor: string | null
}>`
  color: ${(props) => props.textColor};
  font-weight: bold;
  display: block;
  margin-bottom: ${(props) => props.theme.spacing[8]};

  a {
    color: ${(props) => props.linkTextColor} !important;
  }

  @media (min-width: ${(props) => props.theme.breakpoints.md}) {
    text-align: center;
  }
`

const Description = styled(Container)<{
  textColor: string | null
  linkTextColor: string | null
}>`
  color: ${(props) => props.textColor};

  a {
    color: ${(props) => props.linkTextColor} !important;
  }
`

const EditButton = styled(Button)`
  margin-bottom: ${(props) => props.theme.spacing[2]}!important;
`
